import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { ListTutorTask } from '@shared/model'
import type { TutorTask, TutorTaskEditForm, TutorTaskFilePack } from './interfaces'
import type { TutorTaskUpdatePayload } from './types'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'
import { TaskStatus, TaskType } from '@brighterly/lib-core-types/tutor/enums'

import dayjs from 'dayjs'

export const TASK_EMPTY_CELL = '-'

const CLOSED_STATUSES: readonly TaskStatus[] = [TaskStatus.Completed, TaskStatus.Canceled]
const FILE_PACK_TYPES: readonly TaskType[] = [
  TaskType.Certificate,
  TaskType.ProgressReport,
  TaskType.AdditionalPractice,
  TaskType.LearningPlan,
]

/** Postpone and cancel are offered for a task that is still open. */
export const isTaskOpen = (status: TaskStatus): boolean => !CLOSED_STATUSES.includes(status)

/** The tasks whose result is a file pack the admin may relink. */
export const isFilePackTaskType = (type: TaskType): boolean => FILE_PACK_TYPES.includes(type)

/** Only a one-on-one task carries a postpone reason. */
export const isReasonTaskType = (type: TaskType): boolean => type === TaskType.OneOnOne

export const formatTaskDate = (value: string | null): string =>
  value ? dayjs(value).format(DateFormat.KanbanDate) : ''

/** Overdue = the due date passed before the task was finished, or before now while it is open. */
export const isTaskOverdue = (task: Pick<TutorTask, 'due_date' | 'completed_date'>, now: string): boolean => {
  if (!task.due_date) return false

  return !dayjs(task.due_date).isAfter(dayjs(task.completed_date ?? now))
}

/** One select for status and actor: CANCELED is offered only as "Canceled by …" (legacy filter). */
export const buildTaskStatusFilterOptions = (lists: ListTutorTask | null): AppSelectOption<string>[] => {
  if (!lists) return []

  const statuses = Object.entries(lists.status)
    .filter(([value]) => value !== TaskStatus.Canceled)
    .map(([value, text]) => ({ value, text }))
  const canceledLabel = lists.status[TaskStatus.Canceled] ?? 'Canceled'
  const actors = Object.entries(lists.actor_type)
    .map(([value, text]) => ({ value, text: `${canceledLabel} by ${text}` }))

  return [...statuses, ...actors]
}

export const getFilePackOptionText = (pack: TutorTaskFilePack, categories: Record<string, string> | undefined): string =>
  `${categories?.[pack.category] ?? pack.category} (${formatTaskDate(pack.created_at)})`

export const buildTaskEditForm = (task: TutorTask): TutorTaskEditForm => ({
  status: task.status,
  tutor_id: task.tutor_id,
  due_date: task.due_date,
  file_pack_id: task.file_pack_id,
  description: task.description,
})

/** Partial body: an untouched pack is not re-sent, so a save never completes a task by accident. */
export const buildTaskUpdatePayload = (task: TutorTask, form: TutorTaskEditForm): TutorTaskUpdatePayload => {
  const initial = buildTaskEditForm(task)
  const payload: TutorTaskUpdatePayload = {}
  const keys = Object.keys(form) as (keyof TutorTaskEditForm)[]

  keys.forEach((key) => {
    const value = key === 'description' ? (form.description || null) : form[key]
    if (value !== initial[key]) Object.assign(payload, { [key]: value })
  })

  return payload
}
